import { FC, useContext, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { GroupButton } from "../GroupButton";
import { useFormik } from "formik";
import { INIT_IMPORT_FORM } from "./constants/importConsts";
import * as Yup from "yup";
import { IImportExcel, ITemplateImport } from "./models/excelModels";
import { actualFileType } from "../file-upload/uploadFileConsts";
import { toast } from "react-toastify";
import AppContext from "../../../AppContext";
import { exportToFile } from "../../utils/functionUtils";
import { MAX_FILE_SIZE, RESPONSE_STATUS_CODE } from "../../constants/moduleConsts";
import useMultiLanguage from "../../../hook/useMultiLanguage";
import { getErrorFile, getTemplateFile, importFile } from "../../services/fileServices";
import CustomTooltip from "../custom-tooltip";

interface IProps {
  handleClose: () => void;
  allowFileTypes: string;
  template: ITemplateImport;
  handleCloseAndSearch?: () => void;
}

const ImportExcelDialog: FC<IProps> = (props) => {
  const { handleClose, allowFileTypes, template, handleCloseAndSearch } = props;
  const { setPageLoading } = useContext(AppContext);

  const { lang } = useMultiLanguage();
  const acceptTypes = allowFileTypes.split(",").map((type) => type.trim());
  const [errorFileName, setErrorFileName] = useState("");

  const validationSchema = Yup.object().shape({
    file: Yup.mixed().required(lang("VALIDATION.REQUIRE")).nullable()
  });

  const handleSubmit = async (values: IImportExcel) => {
    try {
      setPageLoading(true);
      const { data } = await importFile({ ...values, type: String(template.type) });
      if(data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
        toast.success(lang("TOAST.IMPORT.SUCCESS"));
        handleCloseAndSearch ? handleCloseAndSearch() : handleClose();
      } else {
        setErrorFileName(data?.data || "");
        toast.warning(lang("TOAST.IMPORT.ERROR"));
      }
    } catch (error) {
      toast.error(lang("GENERAL.ERROR"));
    } finally {
      setPageLoading(false);
    }
  }

  const formik = useFormik({
    initialValues: INIT_IMPORT_FORM,
    onSubmit: handleSubmit,
    validationSchema
  });

  const handleChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if(file) {
      const isValidMaxSize = file.size > MAX_FILE_SIZE;
      const isValidFileType = actualFileType.find(item => acceptTypes.includes(item.id) && item.value === file.type);
      if (!isValidFileType) {
        toast.warning(lang("TOAST.ERROR.FORMAT.FILE"));
        return;
      }
      if (isValidMaxSize) {
        toast.warning(lang("TOAST.ERROR.LARGE.FILE"))
        return;
      }
      formik.setFieldValue("file", file);
      setErrorFileName("");
    }
  };

  const handleDownloadTemplate = async () => {
    await exportToFile({
      exportAPI: () => getTemplateFile({ type: template.type }),
      fileName: template.name || "Mẫu dữ liệu",
      messageSuccess: lang("TOAST.DOWNLOAD.SUCCESS"),
      setPageLoading
    });
  }

  const handleDownloadErrorFile = async () => {
    await exportToFile({
      exportAPI: () => getErrorFile({ type: template.type, fileName: errorFileName }),
      fileName: template?.errorName || formik.values?.file?.name?.split('.')?.slice(0, -1)?.join('.') + "_lỗi" || "Dữ liệu lỗi",
      messageSuccess: lang("TOAST.DOWNLOAD.SUCCESS"),
      setPageLoading
    });
  }

  return (
    <Modal
      show={true}
      onHide={handleClose}
      backdrop="static"
      centered
      size="lg"
    >
      <Form onSubmit={formik.handleSubmit}>
        <Modal.Header closeButton className="spaces py-12">
          <Modal.Title className="spaces fs-20 fw-600">{lang("BTN.IMPORT.EXCEL")}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="spaces px-32 py-12">
          <Row>
            <Col xs={12} lg={6}>
              <div className="text-lable-input lable">
                <span>{lang("GENERAL.IMPORT.TEMPLATE")}</span>
              </div>
              <div className="custom-file-upload gap-2">
                <label className="flex flex-middle flex-shrink-1 flex-grow-0 overflow-hidden">
                  <span className="file-preview">
                    {template?.name || ""}
                  </span>
                </label>
                <CustomTooltip title={lang("DOWNLOAD.FILE")} placement="right">
                  <div
                    className="bi bi-download cursor-pointer"
                    onClick={handleDownloadTemplate}
                  ></div>
                </CustomTooltip>
              </div>
            </Col>
            <Col xs={12} lg={6}>
              <div className="text-lable-input lable">
                <span>{lang("GENERAL.IMPORT.FILE")}</span>
              </div>
              <div className={`custom-file-upload gap-2 ${formik.errors?.file && formik.touched?.file && "form-control is-invalid"}`}>
                <CustomTooltip title={lang("SELECT.FILE")} placement="auto">
                  <label className="flex flex-middle flex-shrink-1 flex-grow-0 overflow-hidden cursor-pointer">
                    <input type="file" onChange={handleChangeFile} accept={allowFileTypes} />
                    <div className="icon-upload-file">
                      <i className="fa fa-cloud-upload text-primary me-1" />
                    </div>
                    <span className="file-preview">
                      {formik.values?.file?.name ?? ""}
                    </span>
                  </label>
                </CustomTooltip>
              </div>
              {formik.errors?.file && formik.touched?.file && <div className='invalid-feedback'>{formik.errors.file?.toString()}</div>}
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="spaces py-10">
          <div className="flex flex-center mx-auto gap-6 ps-3">
            <GroupButton type="btn-cancel" handleCloseUpdateDialog={handleClose} />
            <GroupButton typeButton="submit">{lang("BTN.IMPORT.EXCEL")}</GroupButton>
            {errorFileName && <GroupButton handleEvent={handleDownloadErrorFile}>{lang("BTN.VIEW_ERROR")}</GroupButton>}
          </div>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default ImportExcelDialog;